import React, { useRef, useState, useEffect } from "react";
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    addDays,
    isSameDay,
    parse,
    isValid,
    isBefore,
    isAfter,
    startOfDay,
} from "date-fns";
import Button from "./ui/Button";
import { WEEK_NAMES, LEAVE_CHANGES_SUBVIEW } from "../constants";
import CalendarIcon from "./icons/CalendarIcon";
import GTM from "../GTM";
import PropTypes from "prop-types";

const toDateOrFallback = (value, fallback) =>
    value ? new Date(value) : fallback;

const getActiveMaxDate = (focusField, maxStartDate, validEndDate) =>
    focusField === "start" ? maxStartDate : validEndDate;

const Calendar = ({
    startDate: propStartDate,
    endDate: propEndDate,
    handleConfirmButton,
    setShowReportingCalendar,
    reportViewType,
    subviewName,
    minimumStartDate,
    maximumStartDate,
    maximumEndDate,
    setOpenReportingPeriod,
}) => {
    const gtm = new GTM(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [startInput, setStartInput] = useState("");
    const [endInput, setEndInput] = useState("");
    const [showCalendar, setShowCalendar] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [startError, setStartError] = useState(false);
    const [endError, setEndError] = useState(false);
    const [isCalenderClose, setIsCalenderClose] = useState(false);
    const [focusField, setFocusField] = useState(null);
    const yearDropdownRef = useRef(null);

    const dropdownItemsRef = useRef([]);
    const isEndDateDisabled = subviewName === LEAVE_CHANGES_SUBVIEW;

    const inputRefs = {
        start: useRef(null),
        end: useRef(null),
    };

    useEffect(() => {
        if (inputRefs.start.current && inputRefs.end.current) {
            inputRefs.start.current.addEventListener("blur", handleBlur);
            inputRefs.end.current.addEventListener("blur", handleBlur);
        }
        return () => {
            if (inputRefs.start.current) {
                inputRefs.start.current.removeEventListener("blur", handleBlur);
            }
            if (inputRefs.end.current) {
                inputRefs.end.current.removeEventListener("blur", handleBlur);
            }
        };
    }, []);

    const today = new Date();
    const validStartDate = new Date(minimumStartDate);
    const maxDate = maximumEndDate ? new Date(maximumEndDate) : today;
    const maxStartDate = toDateOrFallback(maximumStartDate, maxDate);
    const activeMaxDate = getActiveMaxDate(focusField, maxStartDate, maxDate);
    const years = Array.from(
        {
            length:
                activeMaxDate.getFullYear() - validStartDate.getFullYear() + 1,
        },
        (_, i) => activeMaxDate.getFullYear() - i
    );

    const validEndDate = maxDate;

    const isStartDateAfterMax = (date) =>
        isAfter(startOfDay(date), startOfDay(maxStartDate));

    useEffect(() => {
        if (startDate && startDate < validStartDate) {
            setStartDate(validStartDate);
            setStartInput(format(validStartDate, "MM/dd/yyyy"));
        }
        if (endDate && endDate > maxDate) {
            setEndDate(maxDate);
            setEndInput(format(maxDate, "MM/dd/yyyy"));
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (!startDate) {
            return;
        }
        setStartError(isStartDateAfterMax(startDate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, maximumStartDate, maximumEndDate]);

    useEffect(() => {
        if (
            propStartDate &&
            isValid(parse(propStartDate, "MM/dd/yyyy", new Date()))
        ) {
            const parsedStartDate = parse(
                propStartDate,
                "MM/dd/yyyy",
                new Date()
            );
            setStartDate(parsedStartDate);
            setStartInput(format(parsedStartDate, "MM/dd/yyyy"));
        }
        if (
            propEndDate &&
            isValid(parse(propEndDate, "MM/dd/yyyy", new Date()))
        ) {
            const parsedEndDate = parse(propEndDate, "MM/dd/yyyy", new Date());
            setEndDate(parsedEndDate);
            setEndInput(format(parsedEndDate, "MM/dd/yyyy"));
        }
        if (isEndDateDisabled && !propEndDate) {
            setEndDate(today); // 'today' is the current date
            setEndInput(format(today, "MM/dd/yyyy"));
        }
    }, [propStartDate, propEndDate, reportViewType]);
    // Limit startDate and endDate based on reportingType
    useEffect(() => {
        if (startDate && startDate < validStartDate) {
            setStartDate(validStartDate);
            setStartInput(format(validStartDate, "MM/dd/yyyy"));
        }
        if (endDate && endDate > validEndDate) {
            setEndDate(validEndDate);
            setEndInput(format(validEndDate, "MM/dd/yyyy"));
        }
    }, [startDate, endDate, reportViewType]);

    const resetInput = (value, setInput, setError, setDate) => {
        setInput(value);
        setError(false);
        setDate(null);
    };

    const handleInvalidDate = (setError, setDate) => {
        setError(true);
        setDate(null);
    };

    const formatInputValue = (value) => {
        const cleanedValue = value.replace(/\D/g, "");
        const parts = value.split("/");
        let formattedValue = parts[0] || "";
        if (cleanedValue.length >= 2) formattedValue += "/" + (parts[1] || "");
        if (cleanedValue.length >= 4) formattedValue += "/" + (parts[2] || "");
        return { formattedValue, cleanedValue };
    };

    const handleStartInputChange = (
        value,
        cleanedValue,
        formattedValue,
        inputRefs
    ) => {
        setStartInput(formattedValue);

        if (cleanedValue.length !== 8) {
            setStartError(false);
            setStartDate(null);
            return;
        }

        const parsedStartDate = parse(formattedValue, "MM/dd/yyyy", new Date());
        if (
            !isValid(parsedStartDate) ||
            parsedStartDate < validStartDate ||
            isStartDateAfterMax(parsedStartDate)
        ) {
            handleInvalidDate(setStartError, setStartDate);
            return;
        }

        setStartDate(parsedStartDate);
        setStartError(false);

        if (endDate && parsedStartDate > endDate) {
            setEndDate(parsedStartDate);
            setEndInput(format(parsedStartDate, "MM/dd/yyyy"));
        }

        setTimeout(() => {
            inputRefs.end.current?.focus();
        }, 0);
    };

    const handleEndInputChange = (value, cleanedValue, formattedValue) => {
        setEndInput(formattedValue);

        if (cleanedValue.length !== 8) {
            setEndError(false);
            setEndDate(null);
            return;
        }

        const parsedEndDate = parse(formattedValue, "MM/dd/yyyy", new Date());
        if (
            !isValid(parsedEndDate) ||
            parsedEndDate < validStartDate ||
            parsedEndDate > validEndDate
        ) {
            handleInvalidDate(setEndError, setEndDate);
            return;
        }

        if (startDate && parsedEndDate < startDate) {
            setStartDate(parsedEndDate);
            setStartInput(format(parsedEndDate, "MM/dd/yyyy"));
            setEndDate(startDate);
            setEndInput(format(startDate, "MM/dd/yyyy"));
        } else {
            setEndDate(parsedEndDate);
        }

        setEndError(false);
    };

    const handleInputChange = (event, type) => {
        const value = event.target.value;
        const { formattedValue, cleanedValue } = formatInputValue(value);
        const isDeleting =
            event.nativeEvent.inputType === "deleteContentBackward";

        if (isDeleting) {
            if (type === "start")
                resetInput(value, setStartInput, setStartError, setStartDate);
            else if (type === "end")
                resetInput(value, setEndInput, setEndError, setEndDate);
            return;
        }

        if (type === "start") {
            handleStartInputChange(
                value,
                cleanedValue,
                formattedValue,
                inputRefs
            );
        } else if (type === "end") {
            handleEndInputChange(value, cleanedValue, formattedValue);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                yearDropdownRef.current &&
                !yearDropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleKeyDownDropdown = (e, index) => {
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                if (index < years.length - 1) {
                    dropdownItemsRef.current[index + 1].focus();
                }
                break;
            case "ArrowUp":
                e.preventDefault();
                if (index > 0) {
                    dropdownItemsRef.current[index - 1].focus();
                }
                break;
            case "Enter":
            case " ":
                e.preventDefault();
                handleYearSelection(years[index]);
                break;
            default:
                break;
        }
    };
    const handleGtmEvent = () => {
        gtm.Event(
            "custom_event",
            "button",
            "click",
            "confirm",
            "report detail",
            reportViewType?.toLowerCase(),
            "confirm_date_range",
            "undefined"
        );
    };

    const isPreviousDisabled = () => {
        return (
            currentMonth.getFullYear() === validStartDate.getFullYear() &&
            currentMonth.getMonth() === validStartDate.getMonth()
        );
    };

    const isNextDisabled = () => {
        const activeMax = getActiveMaxDate(
            focusField,
            maxStartDate,
            validEndDate
        );
        return (
            currentMonth.getFullYear() === activeMax.getFullYear() &&
            currentMonth.getMonth() === activeMax.getMonth()
        );
    };

    const renderChevronIcon = (direction) => {
        const isDisabled =
            direction === "previous" ? isPreviousDisabled() : isNextDisabled();
        const chevronDirection = direction === "previous" ? "left" : "right";
        const label =
            direction === "previous"
                ? `Previous month, ${format(subMonths(currentMonth, 1), "MMMM")}`
                : `Next month, ${format(addMonths(currentMonth, 1), "MMMM")}`;
        const iconClass = `fa-solid fa-chevron-${chevronDirection} cursor-pointer border-0 p-0 bg-transparent ${isDisabled ? "disabled" : ""}`;
        const tabIndex = isDisabled ? -1 : 0;
        const style = {
            pointerEvents: isDisabled ? "none" : "auto",
            opacity: isDisabled ? 0.5 : 1,
        };

        return (
            <button
                className={iconClass}
                onClick={() => handleMonthChange(direction)}
                tabIndex={tabIndex}
                aria-label={label}
                onKeyDown={(e) => handleKeyDown(e, direction)}
                aria-live="assertive"
                style={style}
            ></button>
        );
    };

    const renderHeader = () => (
        <div className="header">
            <div className="d-flex align-items-center month">
                {renderChevronIcon("previous")}
                <div id="currentMonth">{format(currentMonth, "MMMM")}</div>
                {renderChevronIcon("next")}
            </div>
            <div
                className="year-dropdown"
                onClick={() => setIsOpen(!isOpen)}
                ref={yearDropdownRef}
                id="year-listbox"
                role="combobox"
                tabIndex={0}
                aria-label="year dropdown"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls="year-listbox-options"
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        setIsOpen(!isOpen);
                    } else if (e.key === "Escape" && isOpen) {
                        setIsOpen(false);
                        e.stopPropagation();
                        e.preventDefault();
                    }
                }}
            >
                {currentMonth.getFullYear()}
                <i
                    className={`fa-solid ${isOpen ? "fa-caret-up" : "fa-caret-down"}`}
                ></i>

                <ul
                    id="year-listbox-options"
                    className={`section-card custom-scrollbar ${isOpen ? "yearCalenderDropdownOpen" : "yearCalenderDropdownClose"}`}
                    aria-label="Select a year"
                    role="listbox"
                >
                    {years.map((year, index) => (
                        <li
                            key={year}
                            ref={(el) => (dropdownItemsRef.current[index] = el)}
                            role="option"
                            onClick={() => handleYearSelection(year)}
                            className={
                                year === currentMonth.getFullYear()
                                    ? "year-selected"
                                    : ""
                            }
                            tabIndex={isOpen ? 0 : -1}
                            onKeyDown={(e) => handleKeyDownDropdown(e, index)}
                            aria-selected={year === currentMonth.getFullYear()}
                        >
                            {year}
                        </li>
                    ))}
                </ul>
            </div>

            <div aria-live="polite" className="sr-only">
                {isOpen
                    ? "Year dropdown expanded."
                    : "Year dropdown collapsed."}
                Currently showing year {currentMonth.getFullYear()}.
            </div>
        </div>
    );

    // Event Handlers
    const handleMonthChange = (direction) => {
        const newMonth =
            direction === "previous"
                ? subMonths(currentMonth, 1)
                : addMonths(currentMonth, 1);
        setCurrentMonth(newMonth);
    };

    const handleKeyDown = (e, direction) => {
        if (e.key === "Enter" || e.key === " ") {
            handleMonthChange(direction);
            e.preventDefault();
        }
    };

    const handleYearSelection = (year) => {
        const newMonth = currentMonth.getMonth();
        let targetMonth = newMonth;

        const proposedDate = new Date(year, newMonth, 1);

        if (
            proposedDate <
            new Date(validStartDate.getFullYear(), validStartDate.getMonth(), 1)
        ) {
            targetMonth = validStartDate.getMonth();
        } else if (
            proposedDate >
            new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)
        ) {
            targetMonth = maxDate.getMonth();
        }

        setCurrentMonth(new Date(year, targetMonth));
    };

    const renderDays = () => {
        const days = [];
        const calendarStartDate = startOfWeek(new Date());
        for (let i = 0; i < 7; i++) {
            const day = format(addDays(calendarStartDate, i), "EEEEE"); // This gets the short day name (e.g., "S" for Sunday)
            const ariaLabel = WEEK_NAMES[i].toLowerCase();

            days.push(
                <div
                    className="col col-center"
                    style={{ position: "relative" }}
                    key={i}
                    aria-label={ariaLabel}
                    role="columnheader"
                    tabIndex="0"
                    aria-live="polite"
                >
                    <span
                        style={{
                            position: "absolute",
                            height: 0,
                            width: 0,
                            overflow: "hidden",
                        }}
                    >
                        {ariaLabel}
                    </span>
                    <span aria-hidden="true">{day}</span>
                </div>
            );
        }

        return (
            <tr className="days row" aria-labelledby="weekdays">
                {days}
            </tr>
        );
    };

    const isOutsideCurrentMonth = (day, monthStart, monthEnd) =>
        day < monthStart || day > monthEnd;

    const isDayDisabled = (day, validStartDate, validEndDate) =>
        isBefore(startOfDay(day), startOfDay(validStartDate)) ||
        isAfter(startOfDay(day), validEndDate);

    const getDayCell = ({
        day,
        today,
        selectedDate,
        validStartDate,
        validEndDate,
        currentMonth,
        handleDateClick,
    }) => {
        const cloneDay = day;
        const isDisabled = isDayDisabled(
            cloneDay,
            validStartDate,
            validEndDate
        );
        const isToday = isSameDay(cloneDay, today);
        const isSelected = isSameDay(day, selectedDate);
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);

        if (isOutsideCurrentMonth(day, monthStart, monthEnd)) {
            return (
                <div
                    className="col cell empty"
                    key={cloneDay}
                    role="cell"
                ></div>
            );
        }

        return (
            <div
                className={`col ${isSelected ? "date-selected" : ""} ${
                    isToday ? "today" : ""
                } ${isDisabled ? "disabled" : ""}`}
                key={cloneDay}
                role="cell"
                style={{
                    width: 36,
                    height: 36,
                    paddingTop: 4,
                    pointerEvents: isDisabled ? "none" : "auto",
                    opacity: isDisabled ? 0.5 : 1,
                }}
            >
                <div
                    role="button"
                    onClick={() => !isDisabled && handleDateClick(cloneDay)}
                    onKeyDown={(e) => {
                        if (
                            (e.key === "Enter" || e.key === " ") &&
                            !isDisabled
                        ) {
                            handleDateClick(cloneDay);
                        }
                    }}
                    tabIndex={0}
                    aria-label={format(cloneDay, "dd")}
                    aria-disabled={isDisabled}
                    aria-selected={isSelected}
                >
                    <span>{format(cloneDay, "d")}</span>
                </div>
            </div>
        );
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const calendarStartDate = startOfWeek(monthStart);
        const today = new Date();
        const effectiveMaxDate = getActiveMaxDate(
            focusField,
            maxStartDate,
            validEndDate
        );

        const rows = [];
        let days = [];
        let day = calendarStartDate;

        while (day <= monthEnd || days.length > 0) {
            for (let i = 0; i < 7; i++) {
                days.push(
                    getDayCell({
                        day,
                        today,
                        selectedDate,
                        validStartDate,
                        validEndDate: effectiveMaxDate,
                        currentMonth,
                        handleDateClick,
                    })
                );
                day = addDays(day, 1);
            }

            rows.push(
                <div
                    className="row"
                    style={{ height: 36 }}
                    key={day}
                    role="row"
                >
                    {days}
                </div>
            );
            days = [];
        }

        return <div className="dates">{rows}</div>;
    };

    const handleDateClick = (clickedDate) => {
        if (isEndDateDisabled) {
            // Set only the startDate; retain the default endDate
            setStartDate(clickedDate);
            setStartInput(format(clickedDate, "MM/dd/yyyy"));
        } else {
            // Handle start and end date selection for other report types
            if (!startDate || (startDate && endDate)) {
                setStartDate(clickedDate);
                setEndDate(null);
                setStartInput(format(clickedDate, "MM/dd/yyyy"));
                setEndInput("");
                setSelectedDate(clickedDate);
            } else {
                const sortedDates = [startDate, clickedDate].sort(
                    (a, b) => a - b
                );
                setStartDate(sortedDates[0]);
                setEndDate(sortedDates[1]);
                setStartInput(format(sortedDates[0], "MM/dd/yyyy"));
                setEndInput(format(sortedDates[1], "MM/dd/yyyy"));
                setSelectedDate(clickedDate);
            }
        }
        // Reset calendar to the current month
        setCurrentMonth(new Date());
        setSelectedDate(new Date());
    };

    useEffect(() => {
        if (isEndDateDisabled) {
            setCurrentMonth(new Date());
            setEndDate(new Date());
        }
    }, [reportViewType]);

    const handleFocus = (field) => {
        setFocusField(field);
    };

    const handleBlur = () => {
        setFocusField(null);
    };
    const handleClose = () => {
        setIsCalenderClose(true);
        setTimeout(() => {
            setShowCalendar(false);
            setIsCalenderClose(false);
        }, 300); // Match the animation duration
    };
    const handleCalendarIconClick = (e, field) => {
        e.stopPropagation();
        handleFocus(field);

        if (field === "start" || (field === "end" && !isEndDateDisabled)) {
            if (showCalendar) {
                handleClose();
            } else {
                setShowCalendar(true);
            }
        }
    };
    useEffect(() => {
        if (startDate && endDate) {
            handleClose();
        }
    }, [endDate, startDate]);

    useEffect(() => {
        if (startDate && !isEndDateDisabled) {
            handleFocus("end");
        } else {
            handleFocus("start");
        }
        if (startDate && endDate) {
            handleFocus("start");
        }
    }, [endDate, reportViewType, startDate]);
    useEffect(() => {
        if (showCalendar) {
            // Give focus to the calendar container
            document.getElementById("calendar-container").focus();
        }
    }, [showCalendar]);

    return (
        <div className="section-card custom-calendar">
            <div className="calendar-header d-flex align-items-center">
                <div>
                    <button
                        className="fa-solid fa-chevron-left border-0 bg-transparent"
                        style={{ color: "#037CB7", cursor: "pointer" }}
                        tabIndex={0}
                        type="button"
                        aria-label="Back to Reporting Period"
                        onClick={(e) => {
                            e.preventDefault();
                            setOpenReportingPeriod(true);
                            setShowReportingCalendar(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                setOpenReportingPeriod(true);
                                setShowReportingCalendar(false);
                                e.stopPropagation(); // Stop propagation to prevent other handlers
                                e.preventDefault(); // Prevent default behavior
                            }
                        }}
                    ></button>
                </div>
                <h2 className="header p-0 m-0">Date range</h2>
            </div>
            <div className="date-inputs">
                <div className="date-input">
                    <label>From</label>
                    <div className="input-box">
                        <input
                            type="text"
                            ref={inputRefs.start}
                            value={startInput}
                            onChange={(e) => {
                                handleInputChange(e, "start");
                                handleFocus("start");
                            }}
                            aria-label="From Date"
                            placeholder="MM/DD/YYYY"
                            maxLength={10}
                            onFocus={() => handleFocus("start")}
                            onBlur={handleBlur}
                            style={{
                                border: startError
                                    ? "2px solid red"
                                    : focusField === "start"
                                      ? "2px solid #037CB7"
                                      : "",
                                backgroundColor:
                                    focusField === "start" ? "#E6F2F8" : "",
                            }}
                        />
                        <button
                            className="icon-inside-input p-0 d-flex border-0 bg-transparent"
                            onClick={(e) => handleCalendarIconClick(e, "start")}
                            tabIndex={0}
                            aria-label="From Date Calendar"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    handleCalendarIconClick(e, "start");
                                    e.preventDefault();
                                }
                            }}
                        >
                            <CalendarIcon />
                        </button>
                    </div>
                </div>
                <div className="date-input">
                    <label>To</label>
                    <div className="input-box">
                        <input
                            type="text"
                            ref={inputRefs.end}
                            value={endInput}
                            onChange={(e) => {
                                handleInputChange(e, "end");
                                handleFocus("end");
                            }}
                            placeholder="MM/DD/YYYY"
                            maxLength={10}
                            aria-label="To Date"
                            disabled={!startDate || isEndDateDisabled}
                            onFocus={() => handleFocus("end")}
                            onBlur={handleBlur}
                            style={{
                                border: endError
                                    ? "2px solid red"
                                    : focusField === "end"
                                      ? "2px solid #037CB7"
                                      : "",
                                backgroundColor:
                                    focusField === "end" ? "#E6F2F8" : "",
                            }}
                        />
                        <button
                            style={
                                isEndDateDisabled
                                    ? { pointerEvents: "none", opacity: "0.4" }
                                    : {}
                            }
                            type="button"
                            className="icon-inside-input p-0 d-flex border-0 bg-transparent"
                            onClick={(e) => handleCalendarIconClick(e, "end")}
                            disabled={isEndDateDisabled}
                            aria-label="To Date Calendar"
                        >
                            <CalendarIcon
                                color={
                                    !startDate || isEndDateDisabled
                                        ? "#cdcdcd"
                                        : ""
                                }
                            />
                        </button>
                    </div>
                </div>
            </div>
            {showCalendar && (
                <div
                    className={`calendar-dropdown ${isCalenderClose ? "hide" : ""} ${focusField === "end" ? "slide-calender" : ""}`}
                    style={{ position: "relative" }}
                >
                    <output
                        id="calendar-container"
                        tabIndex="-1"
                        aria-live="polite"
                        style={{
                            position: "absolute",
                            height: 0,
                            width: 0,
                            overflow: "hidden",
                        }}
                    >
                        {(showCalendar &&
                            focusField === "start" &&
                            "Select start date") ||
                            (showCalendar &&
                                focusField === "end" &&
                                "Select end date")}
                    </output>
                    {renderHeader()}
                    <div role="table">
                        {renderDays()}
                        {renderCells()}
                    </div>
                </div>
            )}
            <div
                className="fs-16 d-flex"
                style={{
                    alignSelf: "end",
                    marginTop: 16,
                    paddingRight: 8,
                    width: 94,
                }}
            >
                <output
                    aria-live="assertive"
                    style={{
                        position: "absolute",
                        width: 0,
                        height: 0,
                        overflow: "hidden",
                    }}
                >
                    {startDate &&
                        endDate &&
                        `Selected Date range from ${format(startDate, "MM/dd/yyyy")} to ${format(endDate, "MM/dd/yyyy")}`}
                </output>
                <Button
                    text="Confirm"
                    onClick={() => {
                        if (startDate && endDate) {
                            handleConfirmButton({
                                startDate: format(startDate, "MM/dd/yyyy"),
                                endDate: format(endDate, "MM/dd/yyyy"),
                            });
                            setShowCalendar(false);
                            setShowReportingCalendar(false);
                            handleGtmEvent();
                        }
                    }}
                    disabled={!startDate || !endDate || startError || endError}
                    aria-label="Confirm"
                />
            </div>
        </div>
    );
};

Calendar.propTypes = {
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    handleConfirmButton: PropTypes.func,
    setShowReportingCalendar: PropTypes.func,
    reportViewType: PropTypes.string,
    minimumStartDate: PropTypes.string,
    maximumStartDate: PropTypes.string,
    maximumEndDate: PropTypes.string,
    setOpenReportingPeriod: PropTypes.func,
};

export default Calendar;
